import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BookOpen, CheckCircle2, ClipboardList, FileText, Loader2, MapPin, Plus, Search, Zap } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api, { API_URL, formatApiError } from "@/lib/api";
import { STATUS_LABELS, STATUS_STYLES } from "@/lib/policyLabels";

function PolicyRow({ p, manage }) {
  return (
    <Link to={`/policies/${p.id}`} data-testid={`policy-row-${p.id}`}
      className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-white p-3 hover:border-primary/50 transition-colors duration-150">
      <span className="font-mono text-xs font-semibold text-slate-500 w-24 shrink-0">{p.reference}</span>
      <span className="text-sm font-medium text-slate-900 flex-1 min-w-[180px]">{p.title}</span>
      <Badge className="bg-slate-100 text-slate-600 border-transparent text-[10px]">{p.category}</Badge>
      {manage && <Badge className={STATUS_STYLES[p.status] || ""}>{STATUS_LABELS[p.status] || p.status}</Badge>}
      {p.requires_formal_review && (
        <Badge className="bg-amber-50 text-amber-700 border-transparent text-[10px]" data-testid={`policy-formal-review-${p.id}`}>Formal review required</Badge>
      )}
      <span className="text-xs text-slate-500 font-mono">v{p.version}</span>
    </Link>
  );
}

export default function PoliciesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("policies:manage");
  const canGovern = hasPermission("location:manage_settings");
  const [tab, setTab] = useState("my");
  const [my, setMy] = useState(null);
  const [library, setLibrary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [manageList, setManageList] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [gpsGov, setGpsGov] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    api.get("/policies/my").then((r) => setMy(r.data.items)).catch((e) => toast.error(formatApiError(e)));
    api.get("/policies/categories").then((r) => setCategories(r.data.items)).catch(() => {});
  }, []);

  const loadLibrary = useCallback(() => {
    setLibrary(null);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (cat) params.set("category", cat);
    api.get(`/policies?${params}`).then((r) => setLibrary(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  }, [q, cat]);

  useEffect(() => { if (tab === "library") loadLibrary(); }, [tab, loadLibrary]);
  useEffect(() => {
    if (tab === "manage" && canManage) api.get("/policies?scope=all").then((r) => setManageList(r.data.items)).catch((e) => toast.error(formatApiError(e)));
    if (tab === "compliance" && canManage) api.get("/policies/compliance").then((r) => setCompliance(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (tab === "calendar" && canManage) api.get("/policies/review-calendar").then((r) => setCalendar(r.data.items)).catch((e) => toast.error(formatApiError(e)));
    if (tab === "gps" && canGovern) api.get("/policies/gps/governance").then((r) => setGpsGov(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (tab === "history") api.get("/policies/my/history").then((r) => setHistory(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  }, [tab, canManage, canGovern]);

  const tabs = [
    ["my", "My Policies"], ["library", "Policy Library"], ["quick", "Quick Procedures"], ["history", "My History"],
    ...(canManage ? [["manage", "Manage"], ["compliance", "Compliance"], ["calendar", "Review Calendar"]] : []),
    ...(canGovern ? [["gps", "GPS Governance"]] : []),
  ];

  const needingAck = (my || []).filter((p) => p.acknowledgement_required && !p.acknowledged);

  const downloadAckReport = async (pol, fmt) => {
    try {
      const r = await api.get(`/policies/${pol.id}/acknowledgement-report`, { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pol.reference}-acknowledgements-v${pol.version || "current"}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };


  return (
    <div className="space-y-6" data-testid="policies-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Policies &amp; Procedures</h1>
          <p className="text-sm text-slate-600 mt-1">The controlled policy library for Deluxe Travel Training.</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <a href={`${API_URL}/policies/manual/pdf`} target="_blank" rel="noreferrer" data-testid="manual-pdf-button"
              className="inline-flex h-11 items-center gap-2 rounded-md border border-border px-4 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <FileText className="h-4 w-4" /> Full manual PDF
            </a>
            <Link to="/policies/new" data-testid="policy-new-button"
              className="inline-flex h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-brand-hover">
              <Plus className="h-4 w-4" /> New policy
            </Link>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-border pb-px" data-testid="policies-tabs">
        {tabs.map(([key, label]) => (
          <button key={key} data-testid={`policies-tab-${key}`} onClick={() => setTab(key)}
            className={`rounded-t-md px-4 py-2.5 text-sm font-medium transition-colors duration-150 min-h-[44px] ${
              tab === key ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"
            }`}>
            {label}
            {key === "my" && needingAck.length > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="my-policies-badge">{needingAck.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "my" && (
        <div className="space-y-4" data-testid="policies-my">
          {!my ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
            <>
              <Card className={needingAck.length ? "border-amber-300" : ""}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ClipboardList className="h-5 w-5 text-primary" /> Policies requiring my acknowledgement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {needingAck.length === 0 ? (
                    <p className="py-4 text-sm text-slate-600" data-testid="ack-all-done">You are up to date — nothing is waiting for your acknowledgement.</p>
                  ) : needingAck.map((p) => (
                    <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50/50 p-3" data-testid={`ack-due-${p.id}`}>
                      <span className="font-mono text-xs text-slate-500">{p.reference}</span>
                      <Link to={`/policies/${p.id}`} className="text-sm font-medium text-primary hover:underline flex-1">{p.title}</Link>
                      {p.opened && !p.acknowledged && <Badge className="bg-blue-50 text-blue-700 border-transparent">Opened</Badge>}
                      <span className="text-xs text-slate-500 font-mono">v{p.version}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-lg">My policy training</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="my-training-table">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-slate-500">
                          <th className="py-2 pr-3">Policy</th><th className="py-2 pr-3">Version</th>
                          <th className="py-2 pr-3">Read</th><th className="py-2 pr-3">Acknowledged</th><th className="py-2">Knowledge check</th>
                        </tr>
                      </thead>
                      <tbody>
                        {my.filter((p) => p.mandatory).map((p) => (
                          <tr key={p.id} className="border-b border-border/60" data-testid={`my-training-row-${p.id}`}>
                            <td className="py-2.5 pr-3">
                              <Link to={`/policies/${p.id}`} className="font-medium text-slate-900 hover:text-primary">{p.title}</Link>
                              <span className="ml-2 font-mono text-xs text-slate-400">{p.reference}</span>
                            </td>
                            <td className="py-2.5 pr-3 font-mono text-xs">v{p.version}</td>
                            <td className="py-2.5 pr-3">{p.opened ? <CheckCircle2 className="h-4 w-4 text-ukgreen" /> : <span className="text-xs text-slate-400">Not yet</span>}</td>
                            <td className="py-2.5 pr-3">{p.acknowledged ? <CheckCircle2 className="h-4 w-4 text-ukgreen" /> : <span className="text-xs text-amber-600">Due</span>}</td>
                            <td className="py-2.5">{!p.has_knowledge_check ? <span className="text-xs text-slate-400">—</span> : p.knowledge_check_passed ? <CheckCircle2 className="h-4 w-4 text-ukgreen" /> : <span className="text-xs text-amber-600">To complete</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">Acknowledgement records that you opened and confirmed a version; it is not treated as proof of understanding or competence.</p>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {tab === "library" && (
        <div className="space-y-4" data-testid="policies-library">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="h-11 pl-9" placeholder="Search title, reference or content…" value={q} data-testid="policy-search-input"
                onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadLibrary()} />
            </div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="policy-category-filter"
              className="h-11 rounded-md border border-border bg-white px-3 text-sm">
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.category} value={c.category}>{c.category} ({c.count})</option>)}
            </select>
            <Button variant="outline" className="h-11" onClick={loadLibrary} data-testid="policy-search-button">Search</Button>
          </div>
          {!library ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : library.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-600" data-testid="policy-search-empty">No published policies match.</p>
          ) : (
            <div className="space-y-2" data-testid="policy-search-results">
              {library.map((p) => <PolicyRow key={p.id} p={p} manage={false} />)}
            </div>
          )}
        </div>
      )}

      {tab === "quick" && <QuickProcedures />}

      {tab === "history" && (
        <Card data-testid="policies-history">
          <CardHeader><CardTitle className="text-lg">My acknowledgement history</CardTitle></CardHeader>
          <CardContent>
            {!history ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : history.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-600">No acknowledgements yet.</p>
            ) : (
              <div className="space-y-2">
                {history.map((a) => (
                  <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3 text-sm" data-testid={`ack-history-${a.id}`}>
                    <span className="font-mono text-xs text-slate-500">{a.policy_reference}</span>
                    <span className="font-medium text-slate-900 flex-1">{a.policy_title}</span>
                    <span className="font-mono text-xs text-slate-500">v{a.version}</span>
                    <span className="text-xs text-slate-600">acknowledged {new Date(a.acknowledged_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "manage" && canManage && (
        <div className="space-y-2" data-testid="policies-manage">
          {!manageList ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : manageList.map((p) => <PolicyRow key={p.id} p={p} manage />)}
        </div>
      )}

      {tab === "compliance" && canManage && (
        <div className="space-y-4" data-testid="policies-compliance">
          {!compliance ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[["Staff", compliance.staff_total], ["Fully up to date", compliance.staff_fully_up_to_date],
                  ["Outstanding acknowledgements", compliance.outstanding.length],
                  ["Policies tracked", compliance.policies.length]].map(([l, v]) => (
                  <Card key={l}><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-slate-900">{v}</p><p className="text-xs text-slate-500">{l}</p></CardContent></Card>
                ))}
              </div>
              <Card>
                <CardHeader><CardTitle className="text-lg">Outstanding acknowledgements</CardTitle></CardHeader>
                <CardContent className="space-y-1.5">
                  {compliance.outstanding.length === 0 ? <p className="text-sm text-slate-600 py-4">Nothing outstanding.</p> :
                    compliance.outstanding.slice(0, 100).map((r, i) => (
                      <div key={i} className="flex flex-wrap gap-2 text-sm rounded-md border border-amber-200 bg-amber-50/50 p-2.5" data-testid={`compliance-outstanding-${i}`}>
                        <span className="font-medium text-slate-900">{r.staff_name}</span>
                        <span className="text-slate-600">has not acknowledged</span>
                        <span className="font-medium text-slate-900">{r.policy_title}</span>
                        <span className="font-mono text-xs text-slate-400 ml-auto">{r.policy_reference}</span>
                      </div>
                    ))}
                  <p className="text-xs text-slate-500 pt-2">{compliance.note}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-lg">Acknowledgement report by policy</CardTitle>
                  <p className="text-xs text-slate-500">Inspection-ready export of who has acknowledged the current published version of each mandatory policy.</p>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="ack-report-table">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-slate-500">
                          <th className="py-2 pr-3">Policy</th><th className="py-2 pr-3">Version</th>
                          <th className="py-2 pr-3">Required</th><th className="py-2 pr-3">Acknowledged</th>
                          <th className="py-2 pr-3">Outstanding</th><th className="py-2">Export</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compliance.policies.map((p) => (
                          <tr key={p.id} className="border-b border-border/60" data-testid={`ack-report-row-${p.id}`}>
                            <td className="py-2.5 pr-3"><Link to={`/policies/${p.id}`} className="font-medium text-slate-900 hover:text-primary">{p.title}</Link>
                              <span className="ml-2 font-mono text-xs text-slate-400">{p.reference}</span></td>
                            <td className="py-2.5 pr-3 text-xs">{p.version || "—"}</td>
                            <td className="py-2.5 pr-3 text-xs">{p.required_count}</td>
                            <td className="py-2.5 pr-3 text-xs text-ukgreen font-medium">{p.acknowledged_count}</td>
                            <td className="py-2.5 pr-3 text-xs">{p.outstanding_count > 0 ? <span className="text-amber-700 font-medium">{p.outstanding_count}</span> : 0}</td>
                            <td className="py-2.5">
                              <div className="flex gap-1.5">
                                <Button variant="outline" className="h-8 px-2.5 text-xs" data-testid={`ack-export-csv-${p.id}`} onClick={() => downloadAckReport(p, "csv")}>CSV</Button>
                                <Button variant="outline" className="h-8 px-2.5 text-xs" data-testid={`ack-export-pdf-${p.id}`} onClick={() => downloadAckReport(p, "pdf")}>PDF</Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {tab === "calendar" && canManage && (
        <Card data-testid="policies-calendar">
          <CardHeader><CardTitle className="text-lg">Policy review calendar</CardTitle></CardHeader>
          <CardContent>
            {!calendar ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="review-calendar-table">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3">Policy</th><th className="py-2 pr-3">Owner</th>
                      <th className="py-2 pr-3">Next review</th><th className="py-2">Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calendar.map((p) => (
                      <tr key={p.id} className="border-b border-border/60" data-testid={`review-row-${p.id}`}>
                        <td className="py-2.5 pr-3"><Link to={`/policies/${p.id}`} className="font-medium text-slate-900 hover:text-primary">{p.title}</Link>
                          <span className="ml-2 font-mono text-xs text-slate-400">{p.reference}</span></td>
                        <td className="py-2.5 pr-3 text-xs text-slate-600">{p.owner_role || "—"}</td>
                        <td className="py-2.5 pr-3 text-xs">{p.next_review_date || "Not configured"}</td>
                        <td className="py-2.5">{p.days_until_review == null ? <span className="text-xs text-slate-400">—</span> : (
                          <Badge className={p.days_until_review < 0 ? "bg-red-50 text-red-700 border-transparent" : p.days_until_review < 60 ? "bg-amber-50 text-amber-700 border-transparent" : "bg-ukgreen-light text-ukgreen border-transparent"}>
                            {p.days_until_review < 0 ? `${Math.abs(p.days_until_review)} overdue` : `${p.days_until_review}d`}
                          </Badge>
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "gps" && canGovern && gpsGov && (
        <div className="space-y-4" data-testid="policies-gps-governance">
          {gpsGov.policy_system_review_required && (
            <Card className="border-red-300 bg-red-50" data-testid="gps-policy-warning">
              <CardContent className="p-4 flex items-center gap-2 text-sm font-medium text-red-800">
                <AlertTriangle className="h-5 w-5" /> {gpsGov.policy_warning_note}
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[["Tracking enabled", gpsGov.config.tracking_enabled ? "Yes" : "No"],
              ["Active tracking sessions", gpsGov.status.active_tracking_sessions],
              ["Active without GPS", gpsGov.status.active_without_gps],
              ["Retention (days)", gpsGov.config.retention_days ?? "Not configured"]].map(([l, v]) => (
              <Card key={l}><CardContent className="p-4 text-center"><p className="text-xl font-bold text-slate-900">{v}</p><p className="text-xs text-slate-500">{l}</p></CardContent></Card>
            ))}
          </div>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><MapPin className="h-5 w-5 text-primary" /> GPS policy & notices</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>GPS policy: <b>{gpsGov.gps_policy ? `${gpsGov.gps_policy.reference} (${gpsGov.gps_policy.status}${gpsGov.gps_policy.version ? ` v${gpsGov.gps_policy.version}` : ""})` : "not found"}</b></p>
              <p>GPS privacy notice: <b>{gpsGov.gps_privacy_notice ? `${gpsGov.gps_privacy_notice.reference} (${gpsGov.gps_privacy_notice.status})` : "not found"}</b></p>
              <p className="text-xs text-slate-500">{gpsGov.accuracy_note}</p>
              <p className="text-xs text-slate-500">{gpsGov.status.note}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-lg">Recent shifts without GPS</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {gpsGov.recent_gps_failures.length === 0 ? <p className="text-sm text-slate-600">None — all recent shifts have GPS active.</p> :
                gpsGov.recent_gps_failures.map((f, i) => (
                  <p key={i} className="text-sm text-slate-700" data-testid={`gps-failure-${i}`}>{f.user_name} — signed in without location at {new Date(f.signed_in_at).toLocaleString()}</p>
                ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function QuickProcedures() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/policies?quick=true").then((r) => setItems(r.data.items)).catch(() => {});
  }, []);
  return (
    <div data-testid="policies-quick">
      {!items ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((p) => (
            <Link key={p.id} to={`/policies/${p.id}`} data-testid={`quick-card-${p.id}`}
              className="rounded-lg border-2 border-red-200 bg-white p-4 hover:border-red-400 hover:shadow-md transition-all duration-150">
              <p className="flex items-center gap-2 text-sm font-bold text-red-700"><Zap className="h-4 w-4" /> {p.title}</p>
              <p className="mt-1 font-mono text-xs text-slate-400">{p.reference}</p>
              <p className="mt-2 text-xs text-slate-500">Tap for the quick-action card and full procedure.</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
